// frontend/packages/business-console/src/contexts/BusinessContext.js

import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { getBusinessBySlug } from "../services/businessService";

const isProduction = process.env.NODE_ENV === "production";

const devError = (...args) => {
  if (isProduction) return;
  // eslint-disable-next-line no-console
  console.error(...args);
};

export const BusinessContext = createContext({
  business: null,
  loading: true,
  error: null,
  refreshBusiness: async () => {},
});

export const BusinessProvider = ({ children, slug }) => {
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBusiness = useCallback(async () => {
    if (!slug) {
      setBusiness(null);
      setError("Business not found.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data } = await getBusinessBySlug(slug);
      setBusiness(data || null);
      if (!data) setError("Business not found.");
    } catch (err) {
      devError("BusinessContext: failed to load business by slug:", slug, err);

      const status = err?.response?.status;
      if (status === 404) setError("Business not found.");
      else setError("Failed to load business.");

      setBusiness(null);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchBusiness().catch((err) => devError("BusinessContext: unhandled fetchBusiness error:", err));
  }, [fetchBusiness]);

  const refreshBusiness = useCallback(async () => {
    await fetchBusiness();
  }, [fetchBusiness]);

  const value = useMemo(
    () => ({
      business,
      loading,
      error,
      refreshBusiness,
    }),
    [business, loading, error, refreshBusiness],
  );

  return <BusinessContext.Provider value={value}>{children}</BusinessContext.Provider>;
};